import { useState } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import { MdExpandMore, MdExpandLess, MdRefresh, MdCheckCircle, MdError, MdSchedule, MdPlayArrow, MdVisibility, MdAdd, MdOutlineMore, MdOutlineMoreHoriz } from 'react-icons/md';
import { gql, useSuspenseQuery, useMutation } from '@apollo/client';
import { withScreen } from '../Screen';
import { ProcessesPage_GetRootProcessesQuery, ProcessesPage_GetRootProcessesQueryVariables, ProcessStatus } from '../../../codegen/graphql';
import Button from '../common/Button';
import { getStatusIcon, getStatusBadge, formatDate, getDuration } from './processesUtils';
import { PROCESS_ROW_FRAGMENT, ProcessRow } from './ProcessRow';

const GET_ROOT_PROCESSES = gql`
  query ProcessesPage_GetRootProcesses($status: ProcessStatus) {
    rootProcesses(status: $status) {
      ...ProcessRow_Process
    }
  }
  ${PROCESS_ROW_FRAGMENT}
`;

const CREATE_TEST_PROCESS = gql`
  mutation ProcessesPage_CreateTestProcess($input: CreateProcessInput!) {
    createProcess(input: $input) {
      ...ProcessRow_Process
    }
  }
  ${PROCESS_ROW_FRAGMENT}
`;

interface Process {
  id: string;
  name: string;
  description?: string | null;
  status: ProcessStatus;
  arguments?: any;
  functionName?: string | null;
  errorLog?: string | null;
  result?: any;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  subProcesses?: Process[];
}



interface ProcessesPageProps {}

function ProcessesPage({}: ProcessesPageProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<ProcessStatus | undefined>(undefined);
  const [createTestProcessMutation] = useMutation(CREATE_TEST_PROCESS, {
    onCompleted: () => {
      refetch();
    },
  });

  const { data, refetch } = useSuspenseQuery<
    ProcessesPage_GetRootProcessesQuery,
    ProcessesPage_GetRootProcessesQueryVariables
  >(GET_ROOT_PROCESSES, {
    // variables: { status: statusFilter },
    fetchPolicy: 'cache-and-network',
  });

  const createTestProcess = () => {
    createTestProcessMutation({ variables: { input: {
      name: 'Test Process',
      description: 'Test Process',
      functionName: 'testRandom',
      args: { name: 'Test' },
    } } });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900">
            Processes ({data.rootProcesses.length})
          </h1>

          <button
            onClick={() => createTestProcess()}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <MdAdd size={20} />
            Create Test Process
          </button>

          <select
            value={statusFilter || ''}
            onChange={(e) => setStatusFilter(e.target.value as ProcessStatus || undefined)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="started">Started</option>
            <option value="partial">Partial</option>
            <option value="success">Success</option>
            <option value="error">Error</option>
          </select>
        </div>


        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <MdRefresh size={20} />
          Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {/* Header Row */}
        <div className="grid grid-cols-12 bg-gray-50 border-b border-gray-200">
          <div className="col-span-6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Process Name
          </div>
          <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </div>
          <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Sub-processes
          </div>
          <div className="col-span-2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Created At
          </div>
        </div>

        {/* Data Rows */}
        <div className="bg-white">
          {data.rootProcesses.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No processes found.
            </div>
          ) : (
            data.rootProcesses.map((process) => (
              <ProcessRow
                key={process.id}
                process={process}
                level={0}
              />
            ))
          )}
        </div>
      </div>

      {/* Nested route for process modal */}
      <Outlet />
    </div>
  );
}

export default withScreen(ProcessesPage);

